/**
 * Ported reward engine — TypeScript port of
 * discount_promotion_service/business_logic/reward_engine.py.
 *
 * Pure functions over a CalcContext. Output is the contract triple
 * { discountAmount, rewardKind, loyaltySignals } plus internal extras
 * (freeItems / freeDelivery / appliedTo*) the checkout UI consumes.
 *
 * FAIL LOUD: an unknown reward kind throws UnknownRewardError — we NEVER
 * silently skip a reward, because skipping = undercharging the customer.
 *
 * Keep this in lock-step with the vendored promotion-calc-contract fixtures.
 * If a fixture breaks this file, the port is wrong — fix the port, not the
 * fixture.
 */

// `fixed` is the contract's absolute discount type (SPEC.md); the reference
// `_pct_or_abs` treats anything that is not "percentage" as a flat value.
export const PROMOTION_CALC_CONTRACT_VERSION = "1.4.0";

export type DiscountType = "percentage" | "fixed";
export type DiscountTarget =
  | "order_total"
  | "matching_items"
  | "cheapest_matching_item"
  | "specific_items";
export type UnitSelection = "lowest_price" | "highest_price";

export type CalcLine = {
  lineId: string;
  itemId: string;
  categoryId?: string | null;
  quantity: number;
  unitPrice: number;
  lineSubtotalAmount: number;
  variantOptionIds?: string[] | null;
  grantedByPromotionId?: string | null;
};

export type CalcContext = {
  branchId?: string;
  currency?: string;
  customerId?: string | null;
  orderRef?: string | null;
  orderLines: CalcLine[];
  itemsSubtotalAmount: number;
  orderSubtotalAmount: number;
};

// ── Reward union — discriminated by `kind` (mechanicType is DELETED) ──
export type OrderDiscount = {
  kind: "order_discount";
  discountType: DiscountType;
  value: number;
  cap?: number | null;
};
export type ItemDiscount = {
  kind: "item_discount";
  discountType: DiscountType;
  value: number;
  cap?: number | null;
  target?: DiscountTarget;
  targetItemIds?: string[] | null;
  /** v1.3.0: discount at most this many whole units (null = all candidate units). */
  maxUnits?: number | null;
  /** v1.3.0: which units `maxUnits` picks first. Default "lowest_price". */
  unitSelection?: UnitSelection | null;
};
export type FreeItem = {
  kind: "free_item";
  itemId: string;
  quantity: number;
  variantId?: string | null;
  /** "same_as_required" = BOGO: the free unit is one the customer had to buy. */
  sourceCondition?: "specific_item" | "same_as_required" | string | null;
};

/** The slice of a promotion's Condition the reward engine reads (BOGO). */
export type RequiredItemCriterion = {
  itemIds?: string[] | null;
  categoryIds?: string[] | null;
  minQuantity?: number | null;
};
export type RewardCondition = {
  requirement?: { requiredItemCriteria?: RequiredItemCriterion[] | null } | null;
} | null;
export type FreeDelivery = { kind: "free_delivery" };
export type Bundle = {
  kind: "bundle";
  itemRequirements: Array<{ itemId: string; quantity: number }>;
  bundlePrice: number;
};
export type CategoryBundleGroup = {
  categoryIds?: string[] | null;
  itemIds?: string[] | null;
  excludeItemIds?: string[] | null;
  quantity?: number | null;
};
/** v1.3.0: "any X + any Y for a fixed price". */
export type CategoryBundle = {
  kind: "category_bundle";
  bundlePrice: number;
  groups: CategoryBundleGroup[];
  maxApplications?: number | null;
};
export type AddOn = { kind: "add_on"; addOnItemId: string; addOnPrice: number };
export type InstantWin = { kind: "instant_win"; winProbability: number; then: Reward };
export type LoyaltyCredit = { kind: "loyalty_credit"; amount: number };
export type EarnMultiplier = { kind: "earn_multiplier"; multiplier: number };
export type PromoCode = { kind: "promo_code"; promotionId?: string };

export type Reward =
  | OrderDiscount
  | ItemDiscount
  | FreeItem
  | FreeDelivery
  | Bundle
  | CategoryBundle
  | AddOn
  | InstantWin
  | LoyaltyCredit
  | EarnMultiplier
  | PromoCode;

export type RewardKind = Reward["kind"];

/** Program-neutral signals; loyalty (not the client) resolves points vs cashback. */
export type LoyaltySignals = { earnMultiplier?: number; loyaltyCredit?: number };

/** lineId -> whole units already discounted by an earlier stacked promotion. */
export type ClaimedUnits = Record<string, number>;

export type GrantedFreeItem = { itemId: string; variantId?: string | null; quantity: number };

export type RewardOutcome = {
  rewardKind: RewardKind;
  discountAmount: number;
  appliedToTarget?: "order" | "line" | null;
  appliedToLineId?: string | null; // may be "DISTRIBUTE"
  freeItems?: GrantedFreeItem[] | null;
  freeItemsToAdd?: GrantedFreeItem[] | null;
  lineAllocations?: Record<string, number>;
  freeDelivery: boolean;
  loyaltySignals: LoyaltySignals;
};

export class UnknownRewardError extends Error {
  constructor(kind: string) {
    super(`No handler for reward kind ${JSON.stringify(kind)}`);
    this.name = "UnknownRewardError";
  }
}

function outcome(kind: RewardKind, patch: Partial<RewardOutcome> = {}): RewardOutcome {
  // SPEC "Output" defaults: not line-based, no allocations.
  return {
    rewardKind: kind,
    discountAmount: 0,
    appliedToLineId: null,
    lineAllocations: {},
    freeDelivery: false,
    loyaltySignals: {},
    ...patch,
  };
}

function pctOrAbs(discountType: DiscountType, value: number, base: number): number {
  // Mirrors reference `_pct_or_abs`: percentage scales the base, everything
  // else (e.g. "fixed") is a flat value.
  return discountType === "percentage" ? base * (value / 100) : value;
}

/**
 * Python `round(x, 2)`: rounds the EXACT binary value of `x`, ties to even
 * (SPEC "Rounding"). `toFixed(60)` exposes the exact decimal expansion of the
 * double, so a true tie is only detected when the double really is x.xx5.
 */
export function pyRound2(x: number): number {
  if (!Number.isFinite(x)) return x;
  const neg = x < 0;
  const s = Math.abs(x).toFixed(60);
  const [intPart, frac] = s.split(".");
  const kept = BigInt(intPart + frac.slice(0, 2));
  const rest = frac.slice(2);
  let up = false;
  if (rest[0] > "5") up = true;
  else if (rest[0] === "5") {
    up = /[1-9]/.test(rest.slice(1)) || kept % 2n === 1n;
  }
  const n = Number(up ? kept + 1n : kept) / 100;
  return neg ? -n : n === 0 ? 0 : n;
}

const round2 = pyRound2;

function clamp(amt: number, cap: number | null | undefined, base: number): number {
  let a = amt;
  if (cap != null) a = Math.min(a, cap);
  a = Math.min(a, base);
  return Math.max(0, a);
}

function orderDiscount(r: OrderDiscount, ctx: CalcContext): RewardOutcome {
  const base = ctx.orderSubtotalAmount;
  const amt = clamp(pctOrAbs(r.discountType, r.value, base), r.cap ?? null, base);
  return outcome("order_discount", { discountAmount: amt, appliedToTarget: "order" });
}

/** Customer-added lines with a real quantity: granted (free) lines and
 *  phantom qty-0 lines never earn another promotion's discount. */
function payableLines(ctx: CalcContext): CalcLine[] {
  return ctx.orderLines.filter((l) => l.quantity > 0 && !l.grantedByPromotionId);
}

function itemDiscount(r: ItemDiscount, ctx: CalcContext, condition?: RewardCondition): RewardOutcome {
  const target = r.target ?? "order_total";
  const lines = payableLines(ctx);
  if (target === "order_total") {
    // SPEC: order-level discount on the order subtotal (ignores maxUnits).
    const base = ctx.orderSubtotalAmount;
    const amt = clamp(pctOrAbs(r.discountType, r.value, base), r.cap ?? null, base);
    return outcome("item_discount", { discountAmount: amt, appliedToTarget: "order" });
  }
  let cands: CalcLine[];
  let appliedLineId: string | null = null;
  if (target === "specific_items") {
    const ids = r.targetItemIds ?? [];
    cands = lines.filter((l) => ids.includes(l.itemId));
  } else if (target === "cheapest_matching_item") {
    cands = lines.length
      ? [lines.reduce((m, l) => (l.unitPrice < m.unitPrice ? l : m))]
      : [];
    if (cands.length) appliedLineId = cands[0].lineId;
  } else {
    // matching_items: the qualifying lines, or every payable line when the
    // promotion has no item requirement.
    const q = qualifyingLines(condition, ctx).filter((l) => l.quantity > 0);
    cands = q.length ? q : lines;
  }
  if (cands.length === 0) return outcome("item_discount");
  let maxUnits = r.maxUnits ?? null;
  if (target === "specific_items" || target === "cheapest_matching_item") {
    // v1.4.0: one discounted unit per time the requirement is met by units
    // outside the reward's own items; maxUnits stays the per-order ceiling.
    const times = qualificationCount(condition, ctx, cands);
    if (times != null) maxUnits = maxUnits == null ? times : Math.min(Math.trunc(maxUnits), times);
  }
  if (maxUnits != null) return itemDiscountMaxUnits(r, ctx, cands, maxUnits);
  const base = cands.reduce((s, l) => s + l.lineSubtotalAmount, 0);
  const amt = clamp(pctOrAbs(r.discountType, r.value, base), r.cap ?? null, base);
  return outcome("item_discount", {
    discountAmount: amt,
    appliedToTarget: "line",
    appliedToLineId: appliedLineId ?? "DISTRIBUTE",
  });
}

function unitValue(l: CalcLine): number {
  return l.quantity ? l.lineSubtotalAmount / l.quantity : 0;
}

type Unit = { pos: number; lineId: string; value: number };

function expandUnits(lines: CalcLine[], ctx: CalcContext): Unit[] {
  const pos = new Map<string, number>();
  ctx.orderLines.forEach((l, i) => {
    if (!pos.has(l.lineId)) pos.set(l.lineId, i);
  });
  const units: Unit[] = [];
  for (const l of lines) {
    if (l.quantity <= 0) continue;
    const n = Math.trunc(l.quantity);
    for (let k = 0; k < n; k++) units.push({ pos: pos.get(l.lineId)!, lineId: l.lineId, value: unitValue(l) });
  }
  return units;
}

function cmp(a: number | string, b: number | string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** SPEC `distribute`: proportional 2dp shares, remainder on the largest weight (earliest on tie). */
export function distribute(total: number, weighted: Array<[string, number]>): Record<string, number> {
  const wsum = weighted.reduce((s, [, w]) => s + w, 0);
  if (weighted.length === 0 || wsum <= 0) return {};
  const shares = weighted.map(([, w]) => round2((total * w) / wsum));
  const remainder = round2(total - shares.reduce((s, x) => s + x, 0));
  if (remainder) {
    let top = 0;
    for (let i = 1; i < weighted.length; i++) if (weighted[i][1] > weighted[top][1]) top = i;
    shares[top] = round2(shares[top] + remainder);
  }
  const alloc: Record<string, number> = {};
  weighted.forEach(([lid], i) => {
    alloc[lid] = round2((alloc[lid] ?? 0) + shares[i]);
  });
  return alloc;
}

function dropZero(alloc: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(alloc)) if (v > 0) out[k] = v;
  return out;
}

function lineSignal(alloc: Record<string, number>): string | null {
  const ids = Object.keys(alloc).filter((k) => alloc[k] > 0);
  if (ids.length === 0) return null;
  return ids.length === 1 ? ids[0] : "DISTRIBUTE";
}

/**
 * v1.4.0 per qualification: how many times the promotion's item requirement is
 * met by units OUTSIDE the reward's own candidate lines, i.e.
 * floor(qualifying units / max(minQuantity)). null when there is no item
 * requirement or every qualifying unit is itself a candidate (pre-1.4.0 rule).
 */
function qualificationCount(condition: RewardCondition | undefined, ctx: CalcContext, cands: CalcLine[]): number | null {
  const criteria = condition?.requirement?.requiredItemCriteria ?? [];
  if (!criteria.length) return null;
  const candIds = new Set(cands.map((l) => l.lineId));
  const outside = qualifyingLines(condition, ctx).filter((l) => !candIds.has(l.lineId) && l.quantity > 0);
  if (!outside.length) return null;
  const need = Math.max(Math.trunc(Math.max(...criteria.map((c) => c.minQuantity ?? 1), 1)), 1);
  return Math.floor(outside.reduce((s, l) => s + Math.trunc(l.quantity), 0) / need);
}

function itemDiscountMaxUnits(r: ItemDiscount, ctx: CalcContext, cands: CalcLine[], maxUnits: number = r.maxUnits ?? 0): RewardOutcome {
  if (Math.trunc(maxUnits) <= 0) return outcome("item_discount");
  const units = expandUnits(cands, ctx);
  if (r.unitSelection === "highest_price") {
    units.sort((a, b) => cmp(b.value, a.value) || cmp(a.pos, b.pos) || cmp(a.lineId, b.lineId));
  } else {
    units.sort((a, b) => cmp(a.value, b.value) || cmp(a.pos, b.pos) || cmp(a.lineId, b.lineId));
  }
  const chosen = units.slice(0, Math.trunc(maxUnits));
  const perUnit: Array<[string, number]> = chosen.map((u) => {
    const d = r.discountType === "percentage" ? u.value * (r.value / 100) : r.value;
    return [u.lineId, round2(Math.max(0, Math.min(d, u.value)))];
  });
  let total = round2(perUnit.reduce((s, [, d]) => s + d, 0));
  let alloc: Record<string, number>;
  if (r.cap != null && total > r.cap) {
    total = round2(r.cap);
    alloc = distribute(total, perUnit);
  } else {
    alloc = {};
    for (const [lid, d] of perUnit) alloc[lid] = round2((alloc[lid] ?? 0) + d);
  }
  alloc = dropZero(alloc);
  if (total <= 0) return outcome("item_discount");
  return outcome("item_discount", {
    discountAmount: total,
    appliedToTarget: "line",
    appliedToLineId: lineSignal(alloc),
    lineAllocations: alloc,
  });
}

function matchesFreeItem(l: CalcLine, r: FreeItem, promotionId: string | null): boolean {
  const grantedBy = l.grantedByPromotionId ?? null;
  if (grantedBy != null) return grantedBy === promotionId;
  if (l.itemId !== r.itemId) return false;
  return r.variantId == null || (l.variantOptionIds ?? []).includes(r.variantId);
}

/**
 * reward_engine.py `_qualifying_lines`: customer-added lines matching any of
 * the condition's requiredItemCriteria (by itemId or categoryId), in criterion
 * then cart order, each line once. Granted lines never qualify.
 */
function qualifyingLines(condition: RewardCondition | undefined, ctx: CalcContext): CalcLine[] {
  const criteria = condition?.requirement?.requiredItemCriteria ?? [];
  if (!criteria.length) return [];
  const candidates = ctx.orderLines.filter((l) => !l.grantedByPromotionId);
  const seen = new Set<string>();
  const matched: CalcLine[] = [];
  for (const crit of criteria) {
    for (const line of candidates) {
      const isMatch =
        (!!crit.itemIds?.length && crit.itemIds.includes(line.itemId)) ||
        (!!crit.categoryIds?.length && !!line.categoryId && crit.categoryIds.includes(line.categoryId));
      if (isMatch && !seen.has(line.lineId)) {
        seen.add(line.lineId);
        matched.push(line);
      }
    }
  }
  return matched;
}

/** Python int(quantity) minus units an earlier stacked promotion claimed. */
function availableUnits(l: CalcLine, claimedUnits?: ClaimedUnits): number {
  return Math.max(0, Math.trunc(l.quantity) - Math.trunc(claimedUnits?.[l.lineId] ?? 0));
}

function compareLineId(a: CalcLine, b: CalcLine): number {
  return a.lineId < b.lineId ? -1 : a.lineId > b.lineId ? 1 : 0;
}

/**
 * Ported from reward_engine.py `_free_item`, `same_as_required` (BOGO) branch.
 *
 * The `need` most expensive qualifying units (need = the largest criterion
 * minQuantity) pay for the deal and are reserved; the cheapest of the rest,
 * plus any units this promotion already granted (which are never reserved),
 * are free. Missing units are asked for via `freeItemsToAdd` as the cheapest
 * qualifying item.
 */
function bogoFreeItem(
  r: FreeItem,
  ctx: CalcContext,
  condition: RewardCondition | undefined,
  promotionId: string | null,
  claimedUnits?: ClaimedUnits,
): RewardOutcome {
  const cands = qualifyingLines(condition, ctx).filter((l) => l.quantity > 0 && !l.grantedByPromotionId);
  const own = promotionId == null ? [] : ctx.orderLines.filter((l) => l.grantedByPromotionId === promotionId);
  const criteria = condition?.requirement?.requiredItemCriteria ?? [];
  const need = Math.trunc(criteria.reduce((m, c) => Math.max(m, Number(c.minQuantity ?? 0)), 0));

  const candUnits: CalcLine[] = [];
  for (const l of cands) for (let i = 0; i < availableUnits(l, claimedUnits); i++) candUnits.push(l);
  candUnits.sort((a, b) => unitValue(b) - unitValue(a) || compareLineId(a, b));
  const ownUnits: CalcLine[] = [];
  for (const l of own) for (let i = 0; i < availableUnits(l, claimedUnits); i++) ownUnits.push(l);
  const freePool = [...candUnits.slice(need), ...ownUnits];
  freePool.sort((a, b) => unitValue(a) - unitValue(b) || compareLineId(a, b));
  const takenUnits = freePool.slice(0, Math.trunc(r.quantity));

  const perLine = new Map<string, number>();
  const lineAllocations: Record<string, number> = {};
  for (const l of takenUnits) {
    perLine.set(l.lineId, (perLine.get(l.lineId) ?? 0) + 1);
    lineAllocations[l.lineId] = round2((lineAllocations[l.lineId] ?? 0) + unitValue(l));
  }
  const taken = takenUnits.length;
  const value = round2(takenUnits.reduce((s, l) => s + unitValue(l), 0));

  let grant: GrantedFreeItem[] | null = null;
  let toAdd: GrantedFreeItem[] | null = null;
  if (cands.length) {
    // min() keeps the first of equal values, as Python's min does.
    const cheapest = cands.reduce((m, l) => (unitValue(l) < unitValue(m) ? l : m));
    grant = [{ itemId: cheapest.itemId, quantity: r.quantity }];
    if (taken < r.quantity) {
      toAdd = [{
        itemId: cheapest.itemId,
        variantId: (cheapest.variantOptionIds ?? [])[0] ?? null,
        quantity: r.quantity - taken,
      }];
    }
  }

  const lineIds = [...perLine.keys()];
  return outcome("free_item", {
    discountAmount: value,
    appliedToTarget: perLine.size ? "line" : null,
    appliedToLineId: lineIds.length === 1 ? lineIds[0] : lineIds.length ? "DISTRIBUTE" : null,
    freeItems: grant,
    freeItemsToAdd: toAdd,
    lineAllocations,
  });
}

/**
 * Ported from reward_engine.py `_free_item` (specific-item branch; BOGO is
 * `bogoFreeItem` above).
 *
 * Discounts units already in the cart in place (own-granted units from a
 * prior round first, then cheapest-per-unit-value paid units, ties broken by
 * the lower `lineId`), and reports any still-missing units via
 * `freeItemsToAdd`.
 */
function freeItem(
  r: FreeItem,
  ctx: CalcContext,
  promotionId: string | null = null,
  condition?: RewardCondition,
  claimedUnits?: ClaimedUnits,
): RewardOutcome {
  if (r.sourceCondition === "same_as_required") return bogoFreeItem(r, ctx, condition, promotionId, claimedUnits);
  const grant: GrantedFreeItem[] | null = r.itemId
    ? [{ itemId: r.itemId, variantId: r.variantId ?? undefined, quantity: r.quantity }]
    : null;

  const cands = ctx.orderLines.filter((l) => l.quantity > 0 && matchesFreeItem(l, r, promotionId));

  const ownUnits: CalcLine[] = [];
  const otherUnits: CalcLine[] = [];
  for (const l of cands) {
    const bucket = (l.grantedByPromotionId ?? null) === promotionId ? ownUnits : otherUnits;
    for (let i = 0; i < availableUnits(l, claimedUnits); i++) bucket.push(l);
  }
  const byValueThenLine = (a: CalcLine, b: CalcLine) =>
    unitValue(a) - unitValue(b) || (a.lineId < b.lineId ? -1 : a.lineId > b.lineId ? 1 : 0);
  ownUnits.sort(byValueThenLine);
  otherUnits.sort(byValueThenLine);
  const takenUnits = [...ownUnits, ...otherUnits].slice(0, Math.trunc(r.quantity));

  const perLine = new Map<string, number>();
  for (const l of takenUnits) perLine.set(l.lineId, (perLine.get(l.lineId) ?? 0) + 1);
  const taken = takenUnits.length;
  const value = round2(takenUnits.reduce((s, l) => s + unitValue(l), 0));

  const toAdd: GrantedFreeItem[] | null =
    r.itemId && taken < r.quantity
      ? [{ itemId: r.itemId, variantId: r.variantId ?? undefined, quantity: r.quantity - taken }]
      : null;

  const lineAllocations: Record<string, number> = {};
  for (const l of takenUnits) {
    lineAllocations[l.lineId] = round2((lineAllocations[l.lineId] ?? 0) + unitValue(l));
  }

  const lineIds = [...perLine.keys()];
  const appliedToLineId = lineIds.length === 1 ? lineIds[0] : lineIds.length ? "DISTRIBUTE" : null;

  return outcome("free_item", {
    discountAmount: value,
    appliedToTarget: perLine.size ? "line" : null,
    appliedToLineId,
    freeItems: grant,
    freeItemsToAdd: toAdd,
    lineAllocations,
  });
}

function freeDelivery(): RewardOutcome {
  return outcome("free_delivery", { freeDelivery: true });
}

function bundle(r: Bundle, ctx: CalcContext): RewardOutcome {
  const need = new Map(r.itemRequirements.map((b) => [b.itemId, b.quantity]));
  const have = new Map<string, number>();
  let actual = 0;
  for (const l of payableLines(ctx)) {
    if (need.has(l.itemId)) {
      have.set(l.itemId, (have.get(l.itemId) ?? 0) + l.quantity);
      actual += l.lineSubtotalAmount;
    }
  }
  for (const [iid, q] of need) {
    if ((have.get(iid) ?? 0) < q) return outcome("bundle"); // incomplete bundle
  }
  return outcome("bundle", {
    discountAmount: Math.max(0, actual - r.bundlePrice),
    appliedToTarget: "line",
    appliedToLineId: "DISTRIBUTE",
  });
}

/**
 * category_bundle (v1.3.0): greedy, deterministic. Each bundle fills the groups
 * in list order with the highest-value unused eligible units; stops when a group
 * can't be filled, maxApplications is reached, or a bundle is not worth more than
 * bundlePrice. Mirrors reference `_category_bundle`.
 */
function categoryBundle(r: CategoryBundle, ctx: CalcContext): RewardOutcome {
  const lines = ctx.orderLines.filter((l) => l.quantity > 0 && !l.grantedByPromotionId);
  const byId = new Map(lines.map((l) => [l.lineId, l]));
  const units = expandUnits(lines, ctx);
  units.sort((a, b) => cmp(b.value, a.value) || cmp(a.pos, b.pos) || cmp(a.lineId, b.lineId));
  const used = new Array<boolean>(units.length).fill(false);

  const eligible = (u: Unit, g: CategoryBundleGroup): boolean => {
    const line = byId.get(u.lineId)!;
    if ((g.excludeItemIds ?? []).includes(line.itemId)) return false;
    return (
      (g.itemIds ?? []).includes(line.itemId) ||
      (line.categoryId != null && (g.categoryIds ?? []).includes(line.categoryId))
    );
  };

  let total = 0;
  let alloc: Record<string, number> = {};
  let applications = 0;
  while (r.maxApplications == null || applications < r.maxApplications) {
    const picked: number[] = [];
    let complete = true;
    for (const g of r.groups) {
      const want = g.quantity ?? 1;
      let taken = 0;
      for (let i = 0; i < units.length && taken < want; i++) {
        if (!used[i] && !picked.includes(i) && eligible(units[i], g)) {
          picked.push(i);
          taken++;
        }
      }
      if (taken < want) {
        complete = false;
        break;
      }
    }
    if (!complete) break;
    const bundleValue = round2(picked.reduce((s, i) => s + units[i].value, 0));
    if (bundleValue <= r.bundlePrice) break;
    for (const i of picked) used[i] = true;
    const disc = round2(bundleValue - r.bundlePrice);
    total = round2(total + disc);
    const d = distribute(disc, picked.map((i) => [units[i].lineId, units[i].value] as [string, number]));
    for (const [lid, amt] of Object.entries(d)) alloc[lid] = round2((alloc[lid] ?? 0) + amt);
    applications++;
  }
  alloc = dropZero(alloc);
  if (total <= 0) return outcome("category_bundle");
  return outcome("category_bundle", {
    discountAmount: total,
    appliedToTarget: "line",
    appliedToLineId: lineSignal(alloc),
    lineAllocations: alloc,
  });
}

function addOn(r: AddOn, ctx: CalcContext): RewardOutcome {
  const line = payableLines(ctx).find((l) => l.itemId === r.addOnItemId);
  if (!line) return outcome("add_on");
  return outcome("add_on", {
    discountAmount: Math.max(0, line.unitPrice - r.addOnPrice),
    appliedToTarget: "line",
    appliedToLineId: line.lineId,
  });
}

function instantWin(
  r: InstantWin,
  ctx: CalcContext,
  promotionId: string | null = null,
  condition?: RewardCondition,
  claimedUnits?: ClaimedUnits,
): RewardOutcome {
  // Reference seeds on f"{customerId}|{orderRef}" and fails closed if either
  // segment stringifies to the missing sentinel ("None" in Python; "null"/""
  // here). No seed → no win.
  const cid = ctx.customerId ?? null;
  const ref = ctx.orderRef ?? null;
  if (cid == null || ref == null || cid === "" || ref === "") {
    return outcome("instant_win"); // no seed → fail closed (lost)
  }
  const seed = `${cid}|${ref}`;
  const roll = (deterministicHash(seed) % 10_000) / 10_000;
  if (roll >= r.winProbability) return outcome("instant_win"); // lost
  const inner = evaluateReward(r.then, ctx, promotionId, condition, claimedUnits);
  return { ...inner, rewardKind: "instant_win" };
}

function loyaltyCredit(r: LoyaltyCredit): RewardOutcome {
  return outcome("loyalty_credit", { loyaltySignals: { loyaltyCredit: Number(r.amount) } });
}

function earnMultiplier(r: EarnMultiplier): RewardOutcome {
  return outcome("earn_multiplier", { loyaltySignals: { earnMultiplier: Number(r.multiplier) } });
}

function promoCode(): RewardOutcome {
  return outcome("promo_code"); // no immediate order effect
}

/**
 * Deterministic, dependency-free integer hash for the instant_win seed roll.
 * The reference uses sha256(seed) % 10000; an exact match would require a JS
 * sha256 (avoided here so the engine stays browser-safe and dependency-free).
 * VERSION 1.0.0's CASES ship NO instant_win golden vector (still true through 1.3.0), so this hash is not
 * pinned by any fixture — it only needs to be stable and deterministic. If the
 * contract later adds an instant_win fixture, swap this for a real sha256 of
 * the seed so the modulus agrees with the reference, and the fixture will pin
 * it.
 */
function deterministicHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

const HANDLERS: {
  [K in RewardKind]: (
    r: Extract<Reward, { kind: K }>,
    ctx: CalcContext,
    promotionId: string | null,
    condition?: RewardCondition,
    claimedUnits?: ClaimedUnits,
  ) => RewardOutcome;
} = {
  order_discount: (r, ctx) => orderDiscount(r, ctx),
  item_discount: (r, ctx, _promotionId, condition) => itemDiscount(r, ctx, condition),
  free_item: (r, ctx, promotionId, condition, claimed) => freeItem(r, ctx, promotionId, condition, claimed),
  free_delivery: () => freeDelivery(),
  bundle: (r, ctx) => bundle(r, ctx),
  category_bundle: (r, ctx) => categoryBundle(r, ctx),
  add_on: (r, ctx) => addOn(r, ctx),
  instant_win: (r, ctx, promotionId, condition, claimed) => instantWin(r, ctx, promotionId, condition, claimed),
  loyalty_credit: (r) => loyaltyCredit(r),
  earn_multiplier: (r) => earnMultiplier(r),
  promo_code: () => promoCode(),
};

/**
 * `promotionId` (optional) is the applying promotion's id — only consulted by
 * `free_item` (and forwarded through `instant_win.then`) to recognize its own
 * previously-granted lines (`grantedByPromotionId`) per SPEC.md's "Taking
 * units" rule. Callers that don't yet round-trip granted lines can omit it.
 * `condition` is the promotion's Condition; BOGO (`free_item` with
 * `sourceCondition: "same_as_required"`) needs it to find the qualifying lines.
 * `claimedUnits` (contract v1.2.0) maps lineId -> whole units an earlier
 * stacked promotion already discounted; free_item treats them as unavailable.
 */
export function evaluateReward(
  reward: Reward,
  ctx: CalcContext,
  promotionId: string | null = null,
  condition?: RewardCondition,
  claimedUnits?: ClaimedUnits,
): RewardOutcome {
  const kind = reward?.kind as RewardKind | undefined;
  const handler = kind
    ? (HANDLERS as Record<
        string,
        (
          r: Reward,
          ctx: CalcContext,
          promotionId: string | null,
          condition?: RewardCondition,
          claimedUnits?: ClaimedUnits,
        ) => RewardOutcome
      >)[kind]
    : undefined;
  if (!handler) {
    // FAIL LOUD — closed union, so this is an unknown/forward-incompatible
    // reward. Never return 0 silently (that would undercharge).
    throw new UnknownRewardError(String(kind));
  }
  return handler(reward as never, ctx, promotionId, condition, claimedUnits);
}
